/**
 * Lightweight client-side SEO/meta manager for ConfirmPH.
 *
 * Why not react-helmet?
 *   - We don't want another dep, and react-helmet's async behaviour breaks the
 *     "Facebook can't see it anyway" reality of pure-SPA crawlers.
 *
 * What this gives you:
 *   - Page title
 *   - description + canonical
 *   - Open Graph (og:title/description/image/url/type/site_name/locale)
 *   - Twitter card (summary_large_image)
 *   - robots (noindex,nofollow when running on a non-production host)
 *   - One or more JSON-LD structured-data blocks (Organization, WebSite,
 *     LocalBusiness, FAQPage, Article: opt in per-page)
 *
 * All elements are marked with `data-confirmph-seo="1"` (or a stable id) so
 * navigating between pages REPLACES previous values instead of stacking.
 */

const SEO_ATTR = "data-confirmph-seo";
const SEO_JSONLD_CLASS = "confirmph-jsonld";

const PROD_SITE_URL_RAW = (process.env.REACT_APP_PUBLIC_SITE_URL || "").trim();
const PROD_HOST = (() => {
  try { return new URL(PROD_SITE_URL_RAW).host || null; } catch { return null; }
})();

/** Is the current browser tab actually on the production domain? */
export function isProductionHost() {
  if (typeof window === "undefined") return false;
  if (!PROD_HOST) return false;
  return window.location.host === PROD_HOST;
}

/** Site base used to build absolute URLs (always returns production base
 *  when configured: we always advertise canonicals as the production
 *  URL even when previewed on staging, so shared/scraped links resolve
 *  to the real site). Falls back to the current origin if no prod URL
 *  has been configured. */
export function siteBaseUrl() {
  if (PROD_SITE_URL_RAW) return PROD_SITE_URL_RAW.replace(/\/$/, "");
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

/** Build an absolute URL from a path or pass-through if already absolute. */
export function absUrl(pathOrUrl) {
  if (!pathOrUrl) return siteBaseUrl();
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = siteBaseUrl();
  return `${base}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

/** Default ConfirmPH brand share image (1200x630 PNG in public/). */
export const DEFAULT_OG_IMAGE = "/og-default.png";

function upsertMeta({ name, property, content }) {
  if (content == null || content === "") return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  const matches = [...document.head.querySelectorAll(selector)];
  let el = matches.find((candidate) => candidate.getAttribute(SEO_ATTR) === "1") || matches[0];
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute(SEO_ATTR, "1");
  el.setAttribute("content", content);
  matches.filter((candidate) => candidate !== el).forEach((candidate) => candidate.remove());
}

function upsertLink({ rel, href, hrefLangCode }) {
  if (!href) return;
  const selector = hrefLangCode
    ? `link[rel="${rel}"][hreflang="${hrefLangCode}"]`
    : `link[rel="${rel}"]`;
  const matches = [...document.head.querySelectorAll(selector)];
  let el = matches.find((candidate) => candidate.getAttribute(SEO_ATTR) === "1") || matches[0];
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hrefLangCode) el.setAttribute("hreflang", hrefLangCode);
    document.head.appendChild(el);
  }
  el.setAttribute(SEO_ATTR, "1");
  el.setAttribute("href", href);
  matches.filter((candidate) => candidate !== el).forEach((candidate) => candidate.remove());
}

function clearJsonLd() {
  document.head.querySelectorAll(`script.${SEO_JSONLD_CLASS}`).forEach((s) => s.remove());
}

function injectJsonLd(blocks) {
  if (!blocks || !blocks.length) return;
  for (const data of blocks) {
    if (!data) continue;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.className = SEO_JSONLD_CLASS;
    try { el.textContent = JSON.stringify(data); } catch { continue; }
    document.head.appendChild(el);
  }
}

/**
 * setSeo({ title, description, canonical, robots, og, twitter, jsonLd })
 *
 *  - canonical: relative path "/rentals/foo" or absolute URL
 *  - robots: "index,follow" (default in production) | "noindex,nofollow"
 *  - og: { title, description, image, url, type, siteName, locale }
 *  - twitter: { card, title, description, image, site }
 *  - jsonLd: array of structured-data objects
 *
 *  Pass `noindex: true` to force noindex regardless of host (used by
 *  private dashboards). Otherwise we automatically inject noindex on
 *  non-production hosts.
 */
export function setSeo({
  title,
  description,
  canonical,
  robots,
  noindex = false,
  og = {},
  twitter = {},
  jsonLd = [],
} = {}) {
  if (typeof document === "undefined") return;

  // Title: append " | ConfirmPH" when it's not already the brand alone.
  const fullTitle = title
    ? (/(\bConfirmPH\b)/i.test(title) ? title : `${title} | ConfirmPH`)
    : "ConfirmPH: Booking Confirmation";
  document.title = fullTitle;

  // Robots: explicit > auto host detection.
  const onProd = isProductionHost();
  const robotsValue = noindex
    ? "noindex,nofollow"
    : (robots || (onProd ? "index,follow" : "noindex,nofollow"));
  upsertMeta({ name: "robots", content: robotsValue });

  // Description
  upsertMeta({ name: "description", content: description });

  // Canonical
  upsertLink({ rel: "canonical", href: canonical ? absUrl(canonical) : null });

  // Open Graph
  const ogUrl = absUrl(og.url || canonical || (typeof window !== "undefined" ? window.location.pathname : "/"));
  upsertMeta({ property: "og:type",        content: og.type || "website" });
  upsertMeta({ property: "og:site_name",   content: og.siteName || "ConfirmPH" });
  upsertMeta({ property: "og:locale",      content: og.locale || "en_PH" });
  upsertMeta({ property: "og:title",       content: og.title || fullTitle });
  upsertMeta({ property: "og:description", content: og.description || description });
  upsertMeta({ property: "og:url",         content: ogUrl });
  upsertMeta({ property: "og:image",       content: absUrl(og.image || DEFAULT_OG_IMAGE) });
  if (og.imageAlt) upsertMeta({ property: "og:image:alt", content: og.imageAlt });

  // Twitter
  upsertMeta({ name: "twitter:card",        content: twitter.card || "summary_large_image" });
  upsertMeta({ name: "twitter:title",       content: twitter.title || og.title || fullTitle });
  upsertMeta({ name: "twitter:description", content: twitter.description || og.description || description });
  upsertMeta({ name: "twitter:image",       content: absUrl(twitter.image || og.image || DEFAULT_OG_IMAGE) });
  if (twitter.site) upsertMeta({ name: "twitter:site", content: twitter.site });

  // Structured data
  clearJsonLd();
  injectJsonLd(jsonLd);
}

/** Reset only the JSON-LD when unmounting (called by <SEO /> on cleanup
 *  for components that own structured data). Meta tags are left in place
 *  because the next page mount will overwrite them. */
export function clearStructuredData() {
  clearJsonLd();
}

/** Static structured-data fragments reusable across the site. */
export function organizationJsonLd() {
  const base = siteBaseUrl();
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ConfirmPH",
    url: base || undefined,
    logo: absUrl("/icon-512.png"),
    sameAs: [],
    description:
      "ConfirmPH helps renters and rental businesses manage booking confirmations, reminders, rental records, inspections, and clearer rental communication in the Philippines.",
    areaServed: { "@type": "Country", name: "Philippines" },
  };
}

export function websiteJsonLd() {
  const base = siteBaseUrl();
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ConfirmPH",
    url: base || undefined,
    potentialAction: {
      "@type": "SearchAction",
      target: `${base}/rentals?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}
